/**
 * MessagesSearchBar — internal search for /mensajes (issue #427)
 *
 * Sits above the sidebar tabs/list. Searches by TEXT (conversation name,
 * project title, participant name, and message content) and/or by DATE
 * RANGE (conversation `last_message_at` / message `created_at`). Results are
 * grouped into "Conversaciones" and "Mensajes", each distinguishing
 * group/individual/project. This is deliberately separate from the GLOBAL
 * command palette (#419/#420) — it only searches within Mensajes, scoped to
 * the current user's conversations (RLS-enforced by the underlying queries).
 *
 * Data sources (reused, not reinvented — see chatService.js):
 *  - Conversations bucket: filtered client-side over the already-loaded
 *    `conversations` list (no extra round trip for the common case).
 *  - Participant names for GROUP/PROJECT conversations: one batch query
 *    (DM counterpart names already arrive on `conversation.dmParticipant`
 *    from chatService.getConversations).
 *  - Messages bucket: chatService.searchMessages(term, options), extended in
 *    this issue to accept `dateFrom`/`dateTo` for date-only search.
 *
 * @module components/messages/MessagesSearchBar
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CONVERSATION_TYPE, searchMessages } from '../../services/chatService';
import { supabase } from '../../services/supabase';
import './MessagesSearchBar.css';

const DEBOUNCE_MS = 250;
const MESSAGE_RESULTS_LIMIT = 20;
const MIN_TEXT_LENGTH = 2;

const normalize = (str) =>
  (str || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const conversationTitle = (conversation) => {
  const isDm = conversation.type === CONVERSATION_TYPE.DM;
  const isGroup = conversation.type === CONVERSATION_TYPE.GROUP;
  const isProject = conversation.type === CONVERSATION_TYPE.PROJECT;
  if (isDm) return conversation.dmParticipant?.full_name || 'Mensaje directo';
  return (
    conversation.name ||
    conversation.project?.title ||
    (isGroup ? 'Grupo' : isProject ? 'Proyecto' : 'Conversacion')
  );
};

const conversationTypeLabel = (type) => {
  if (type === CONVERSATION_TYPE.DM) return 'DM';
  if (type === CONVERSATION_TYPE.GROUP) return 'Grupo';
  return 'Proyecto';
};

const conversationTypeIcon = (type) => {
  if (type === CONVERSATION_TYPE.DM) return 'bi-person';
  if (type === CONVERSATION_TYPE.GROUP) return 'bi-people';
  return 'bi-folder-fill';
};

/**
 * Batch-fetch participant full names for GROUP/PROJECT conversations, used
 * to match "buscar por nombre de participante" for non-DM conversations.
 */
export const fetchParticipantNames = async (conversations) => {
  const ids = (conversations || [])
    .filter((c) => c.type !== CONVERSATION_TYPE.DM)
    .map((c) => c.id);
  if (ids.length === 0) return {};

  const { data, error } = await supabase
    .from('chat_participants')
    .select('conversation_id, user:profiles!user_id(full_name)')
    .in('conversation_id', ids);

  if (error) {
    console.error('[MESSAGES-SEARCH] fetchParticipantNames error:', error);
    return {};
  }

  const map = {};
  for (const row of data || []) {
    if (!map[row.conversation_id]) map[row.conversation_id] = [];
    if (row.user?.full_name) map[row.conversation_id].push(row.user.full_name);
  }
  return map;
};

const isWithinRange = (isoTimestamp, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return true;
  if (!isoTimestamp) return false;
  const ts = new Date(isoTimestamp);
  if (dateFrom && ts < new Date(dateFrom)) return false;
  if (dateTo && ts > new Date(`${dateTo}T23:59:59.999`)) return false;
  return true;
};

const MessagesSearchBar = ({
  conversations = [],
  onSelectConversation,
  onSelectMessage,
  onActiveChange,
}) => {
  const [term, setTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [messageResults, setMessageResults] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(term), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  const { data: participantNames = {} } = useQuery({
    queryKey: ['messages-search-participants', conversations.map((c) => c.id).join(',')],
    queryFn: () => fetchParticipantNames(conversations),
    enabled: conversations.length > 0,
    staleTime: 1000 * 60,
  });

  const trimmedTerm = debouncedTerm.trim();
  const hasDateFilter = !!(dateFrom || dateTo);
  const isTextActive = trimmedTerm.length >= MIN_TEXT_LENGTH;
  const isActive = isTextActive || hasDateFilter;

  useEffect(() => {
    onActiveChange?.(isActive);
  }, [isActive, onActiveChange]);

  // -----------------------------------------------------------------
  // Conversations bucket — client-side filter over the already-loaded list.
  // -----------------------------------------------------------------
  const matchedConversations = useMemo(() => {
    if (!isActive) return [];
    const normTerm = normalize(trimmedTerm);

    return conversations.filter((c) => {
      let textMatch = true;
      if (isTextActive) {
        const haystacks = [
          conversationTitle(c),
          c.project?.title,
          c.project?.project_number,
          ...(participantNames[c.id] || []),
        ];
        textMatch = haystacks.some((h) => h && normalize(h).includes(normTerm));
      }
      const dateMatch = isWithinRange(c.last_message_at || c.created_at, dateFrom, dateTo);
      return textMatch && dateMatch;
    });
  }, [conversations, trimmedTerm, isTextActive, dateFrom, dateTo, participantNames, isActive]);

  // -----------------------------------------------------------------
  // Messages bucket — chatService.searchMessages (text and/or date range).
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!isActive) {
      setMessageResults([]);
      setMessagesLoading(false);
      return undefined;
    }

    let cancelled = false;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setMessagesLoading(true);

    searchMessages(isTextActive ? trimmedTerm : '', {
      limit: MESSAGE_RESULTS_LIMIT,
      dateFrom: dateFrom || undefined,
      // End-of-day boundary so the selected "hasta" date is inclusive
      // (matches the `isWithinRange` boundary used for the conversations
      // bucket below).
      dateTo: dateTo ? `${dateTo}T23:59:59.999` : undefined,
    })
      .then(({ data, error }) => {
        if (cancelled || requestIdRef.current !== requestId) return;
        setMessageResults(error ? [] : data || []);
        setMessagesLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setMessageResults([]);
          setMessagesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [trimmedTerm, isTextActive, hasDateFilter, dateFrom, dateTo, isActive]);

  const handleClear = useCallback(() => {
    setTerm('');
    setDebouncedTerm('');
    setDateFrom('');
    setDateTo('');
    setShowDateFilter(false);
  }, []);

  const handleSelectMessage = useCallback(
    (message) => {
      onSelectMessage?.(message);
    },
    [onSelectMessage]
  );

  const handleSelectConversationResult = useCallback(
    (conversation) => {
      onSelectConversation?.(conversation.id);
    },
    [onSelectConversation]
  );

  const noResults =
    isActive && !messagesLoading && matchedConversations.length === 0 && messageResults.length === 0;

  return (
    <div className={`messages-search ${isActive ? 'is-active' : ''}`}>
      <div className="messages-search-input-row">
        <i className="bi bi-search messages-search-icon" aria-hidden="true"></i>
        <input
          type="text"
          className="messages-search-input"
          placeholder="Buscar conversaciones o mensajes..."
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          aria-label="Buscar en mensajes"
          data-testid="messages-search-input"
        />
        {(term || hasDateFilter) && (
          <button
            type="button"
            className="messages-search-clear-btn"
            onClick={handleClear}
            aria-label="Limpiar busqueda"
            data-testid="messages-search-clear"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        )}
        <button
          type="button"
          className={`messages-search-date-toggle ${showDateFilter || hasDateFilter ? 'active' : ''}`}
          onClick={() => setShowDateFilter((v) => !v)}
          aria-label="Filtrar por fecha"
          aria-expanded={showDateFilter}
          title="Filtrar por fecha"
          data-testid="messages-search-date-toggle"
        >
          <i className="bi bi-calendar3"></i>
        </button>
      </div>

      {showDateFilter && (
        <div className="messages-search-date-row" data-testid="messages-search-date-row">
          <label className="messages-search-date-field">
            <span>Desde</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="messages-search-date-from"
            />
          </label>
          <label className="messages-search-date-field">
            <span>Hasta</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="messages-search-date-to"
            />
          </label>
        </div>
      )}

      {isActive && (
        <div className="messages-search-results" data-testid="messages-search-results">
          {messagesLoading && matchedConversations.length === 0 && messageResults.length === 0 ? (
            <div className="messages-search-loading" data-testid="messages-search-loading">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              Buscando...
            </div>
          ) : noResults ? (
            <div className="messages-search-empty" data-testid="messages-search-empty">
              <i className="bi bi-search"></i>
              <p>Sin resultados{trimmedTerm ? ` para "${trimmedTerm}"` : ''}.</p>
            </div>
          ) : (
            <>
              {matchedConversations.length > 0 && (
                <div className="messages-search-group">
                  <div className="messages-search-group-label">Conversaciones</div>
                  {matchedConversations.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="messages-search-result-item"
                      onClick={() => handleSelectConversationResult(c)}
                      data-testid={`messages-search-result-conversation-${c.id}`}
                    >
                      <span className="messages-search-result-icon">
                        <i className={`bi ${conversationTypeIcon(c.type)}`}></i>
                      </span>
                      <span className="messages-search-result-body">
                        <span className="messages-search-result-title">{conversationTitle(c)}</span>
                        <span className="messages-search-result-meta">
                          <span className="messages-type-pill">{conversationTypeLabel(c.type)}</span>
                          {c.type !== CONVERSATION_TYPE.PROJECT && c.project?.title
                            ? c.project.title
                            : c.type === CONVERSATION_TYPE.PROJECT
                            ? 'Chat de proyecto'
                            : ''}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {messageResults.length > 0 && (
                <div className="messages-search-group">
                  <div className="messages-search-group-label">Mensajes</div>
                  {messageResults.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className="messages-search-result-item"
                      onClick={() => handleSelectMessage(m)}
                      data-testid={`messages-search-result-message-${m.id}`}
                    >
                      <span className="messages-search-result-icon">
                        <i className={`bi ${conversationTypeIcon(m.conversation?.type)}`}></i>
                      </span>
                      <span className="messages-search-result-body">
                        <span className="messages-search-result-title">
                          {m.conversation?.name || m.sender?.full_name || 'Conversacion'}
                        </span>
                        <span className="messages-search-result-snippet">
                          <strong>{m.sender?.full_name || 'Alguien'}:</strong>{' '}
                          {m.content || '(archivo adjunto)'}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MessagesSearchBar;
